import { TrendingUp, TrendingDown, Package, Truck, AlertTriangle, Clock, BarChart3, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const fmt = (v, type) => {
  if (v === null || v === undefined) return "-";
  if (type === "inr") {
    const abs = Math.abs(v);
    if (abs >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(2)} Cr`;
    if (abs >= 1_00_000) return `${(v / 1_00_000).toFixed(2)} L`;
    return v.toLocaleString("en-IN");
  }
  if (type === "pct") return `${v}%`;
  if (type === "ratio") return `${v}x`;
  if (type === "days") return `${v} days`;
  if (type === "count") return v.toLocaleString();
  return v;
};

const KPICard = ({ label, value, type, benchmark, icon: Icon, color, reason }) => {
  const display = fmt(value, type);
  const isBad = benchmark && value !== null && value !== undefined && (
    (benchmark.direction === "higher" && value < benchmark.threshold) ||
    (benchmark.direction === "lower" && value > benchmark.threshold)
  );
  return (
    <Card className={`card-industrial ${isBad ? "border-red-300" : ""}`} data-testid={`sc-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
          {Icon && <Icon className={`w-4 h-4 ${color || "text-muted-foreground"}`} />}
        </div>
        <p className="text-2xl font-bold tracking-tight">{display}</p>
        {display === "-" && reason && <p className="text-[11px] leading-tight text-muted-foreground mt-1">{reason}</p>}
        {benchmark && (
          <p className={`text-xs mt-1 ${isBad ? "text-red-500" : "text-emerald-600"}`}>
            Target: {benchmark.direction === "higher" ? ">" : "<"} {fmt(benchmark.threshold, type)}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const SCKPIDashboard = ({ kpis, reasons = {} }) => {
  if (!kpis) return null;

  const sections = [
    {
      title: "Inventory Health",
      items: [
        { label: "Total Stock Value", key: "total_stock_value", type: "inr", icon: Package, color: "text-blue-600" },
        { label: "Unique Materials", key: "unique_materials", type: "count", icon: BarChart3 },
        { label: "Below Reorder", key: "below_reorder_count", type: "count", icon: AlertTriangle, color: "text-amber-500" },
        { label: "Critical Stock", key: "critical_count", type: "count", icon: AlertTriangle, color: "text-red-500" },
        { label: "Inventory Turnover", key: "inventory_turnover", type: "ratio", icon: TrendingUp, benchmark: { threshold: 6, direction: "higher" } },
        { label: "Days of Supply", key: "days_of_supply", type: "days", icon: Clock },
        { label: "Stockout Rate", key: "stockout_rate", type: "pct", icon: TrendingDown, color: "text-red-500", benchmark: { threshold: 2, direction: "lower" } },
      ],
    },
    {
      title: "Delivery Performance",
      items: [
        { label: "Inbound OTIF", key: "otif_inbound", type: "pct", icon: Truck, color: "text-green-600", benchmark: { threshold: 95, direction: "higher" } },
        { label: "Outbound OTIF", key: "otif_outbound", type: "pct", icon: Truck, color: "text-blue-600", benchmark: { threshold: 95, direction: "higher" } },
        { label: "Fill Rate", key: "fill_rate", type: "pct", icon: CheckCircle, color: "text-green-600", benchmark: { threshold: 98, direction: "higher" } },
        { label: "Avg Lead Time", key: "avg_lead_time", type: "days", icon: Clock },
        { label: "Inbound Deliveries", key: "inbound_deliveries", type: "count", icon: Truck },
        { label: "Outbound Deliveries", key: "outbound_deliveries", type: "count", icon: Truck },
      ],
    },
    {
      title: "Material Flow",
      items: [
        { label: "GR Value", key: "gr_value", type: "inr", icon: TrendingUp, color: "text-green-600" },
        { label: "GI Value", key: "gi_value", type: "inr", icon: TrendingDown, color: "text-orange-500" },
        { label: "Total Movements", key: "movements_count", type: "count", icon: BarChart3 },
      ],
    },
  ];

  return (
    <div className="space-y-6" data-testid="sc-kpi-dashboard">
      {sections.map((sec) => (
        <div key={sec.title}>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">{sec.title}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {sec.items.map((item) => (
              <KPICard
                key={item.key}
                label={item.label}
                value={kpis[item.key]}
                type={item.type}
                benchmark={item.benchmark}
                icon={item.icon}
                color={item.color}
                reason={reasons[item.key]}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SCKPIDashboard;
